import { useCallback, useMemo } from 'react';

import { useCrossFlow } from '@atlassiansox/cross-flow-support/trello';
import { Analytics } from '@trello/atlassian-analytics';
import { useFeatureGate } from '@trello/feature-gate-client';
import { useHasValidAaSession } from '@trello/heartbeat/session';

import {
  useCrossFlowForDeFactoBandits,
  type CrossFlowForDefactoBanditsExperimentVariations,
} from './experimental/useCrossFlowForDeFactoBandits';
import {
  useCrossFlowForEnterpriseSelfServe,
  type CrossFlowForEnterpriseSelfServeExperimentVariations,
} from './experimental/useCrossFlowForEnterpriseSelfServe';
import { useCrossFlowForFreePpmUsersExperience } from './experimental/useCrossFlowForFreePpmUsersExperience';
import { useCrossFlowForNewUserExperience } from './experimental/useCrossFlowForNewUserExperience';
import { useCrossFlowForNonPpmMultiPlayerExperience } from './experimental/useCrossFlowForNonPpmMultiPlayerExperience';
import { useCrossFlowForNonPpmSinglePlayerExperience } from './experimental/useCrossFlowForNonPpmSinglePlayerExperience';
import { useCrossFlowForOnboardingUserExperience } from './experimental/useCrossFlowForOnboardingUserExperience';
import { useCrossFlowForPaidUsersExperience } from './experimental/useCrossFlowForPaidUsersExperience';
import type { TouchpointSourceType } from './TouchpointSourceType';
import {
  trelloCrossFlowOpen,
  type TrelloCrossFlow,
  type TrelloCrossFlowOptions,
} from './TrelloCrossFlow';

/**
 * Union type of all possible variations for crossFlow experiments
 */
export type ExperimentVariations =
  | TrelloCrossFlow<CrossFlowForDefactoBanditsExperimentVariations>
  | TrelloCrossFlow<CrossFlowForEnterpriseSelfServeExperimentVariations>;

export const useTrelloCrossFlow = (
  source: TouchpointSourceType,
  {
    workspaceId,
    boardId,
    isNewTrelloUser,
    jiraTemplateId,
    shouldSkipMarketingPage,
    enterpriseId,
  }: TrelloCrossFlowOptions = {},
): ExperimentVariations => {
  const crossFlow = useCrossFlow();

  const [hasValidAaSession] = useHasValidAaSession();

  const analyticsContainers = useMemo(
    () => ({
      organization: {
        id: workspaceId,
      },
      board: {
        id: boardId,
      },
    }),
    [workspaceId, boardId],
  );

  const { value: doSendOperationalEvent } = useFeatureGate(
    'trello_xf_experiment_analytics_toggle',
  );

  const sendOperationalEvent = useCallback(
    (operationalEventFn: () => void) => {
      if (doSendOperationalEvent) {
        operationalEventFn();
      }
    },
    [doSendOperationalEvent],
  );

  const crossFlowForDeFactoBandits = useCrossFlowForDeFactoBandits({
    source,
    workspaceId,
    hasValidAaSession,
  });

  const crossFlowForEnterpriseSelfServe = useCrossFlowForEnterpriseSelfServe({
    source,
    workspaceId,
    enterpriseId,
    hasValidAaSession,
  });

  /**
   * Hooks for productionized experiences
   *
   */
  const crossFlowForNewUserExperience = useCrossFlowForNewUserExperience({
    source,
    workspaceId,
    boardId,
    hasValidAaSession,
    jiraTemplateId,
  });

  const crossFlowForOnboardingUserExperience =
    useCrossFlowForOnboardingUserExperience({ hasValidAaSession, source });

  const defaultPaidUsersCrossFlowExperience =
    useCrossFlowForPaidUsersExperience({
      source,
      workspaceId,
      boardId,
      hasValidAaSession,
    });

  const defaultSinglePlayerCrossFlowExperience =
    useCrossFlowForNonPpmSinglePlayerExperience({
      source,
      workspaceId,
      boardId,
      hasValidAaSession,
    });

  const defaultMultiPlayerCrossFlowExperience =
    useCrossFlowForNonPpmMultiPlayerExperience({
      source,
      workspaceId,
      boardId,
      hasValidAaSession,
    });

  const defaultFreePpmCrossFlowExperience =
    useCrossFlowForFreePpmUsersExperience({
      source,
      workspaceId,
      boardId,
      hasValidAaSession,
    });

  const trelloCrossFlow = {
    open: trelloCrossFlowOpen(crossFlow, source, [], hasValidAaSession, {
      workspaceId,
      boardId,
      isNewTrelloUser,
      shouldSkipMarketingPage,
    }),
    getIsTargeted: () =>
      crossFlowForDeFactoBandits.getIsTargeted() ||
      crossFlowForEnterpriseSelfServe.getIsTargeted() ||
      defaultPaidUsersCrossFlowExperience.getIsTargeted() ||
      defaultSinglePlayerCrossFlowExperience.getIsTargeted() ||
      defaultMultiPlayerCrossFlowExperience.getIsTargeted() ||
      defaultFreePpmCrossFlowExperience.getIsTargeted() ||
      crossFlowForOnboardingUserExperience.getIsTargeted() ||
      crossFlowForNewUserExperience.getIsTargeted(),
    getExperimentCohort: () => null,
    isEligible: false,
  };

  const { value: isDiscoveryAdExperienceEnabled } = useFeatureGate(
    'trello_xf_discovery_ads_control',
  );

  // Return early if crossFlow is disabled (missing context provider, incompatible version...)
  if (!crossFlow.isEnabled || !isDiscoveryAdExperienceEnabled) {
    return trelloCrossFlow;
  }

  // Check if the current source is eligible for enterprise self-serve experiment
  if (crossFlowForEnterpriseSelfServe.isEligible) {
    sendOperationalEvent(() => {
      Analytics.sendOperationalEvent({
        action: 'enrolled',
        actionSubject: 'crossFlowExperiment',
        source,
        containers: analyticsContainers,
        attributes: {
          experiment: 'crossFlowForEnterpriseSelfServe',
        },
      });
    });
    return crossFlowForEnterpriseSelfServe;
  }

  // Check if the current source is eligible for the de facto bandits experiment
  if (crossFlowForDeFactoBandits.isEligible) {
    sendOperationalEvent(() => {
      Analytics.sendOperationalEvent({
        action: 'enrolled',
        actionSubject: 'crossFlowExperiment',
        source,
        containers: analyticsContainers,
        attributes: {
          experiment: 'crossFlowForDeFactoBandits',
        },
      });
    });
    return crossFlowForDeFactoBandits;
  }

  if (defaultPaidUsersCrossFlowExperience.isEligible) {
    sendOperationalEvent(() => {
      Analytics.sendOperationalEvent({
        action: 'served',
        actionSubject: 'crossFlowExperience',
        source,
        containers: analyticsContainers,
        attributes: {
          experience: defaultPaidUsersCrossFlowExperience.experience,
        },
      });
    });

    return {
      ...defaultPaidUsersCrossFlowExperience,
      getExperimentCohort: () => null,
    };
  }

  if (defaultSinglePlayerCrossFlowExperience.isEligible) {
    sendOperationalEvent(() => {
      Analytics.sendOperationalEvent({
        action: 'served',
        actionSubject: 'crossFlowExperience',
        source,
        containers: analyticsContainers,
        attributes: {
          experience: defaultSinglePlayerCrossFlowExperience.experience,
        },
      });
    });

    return {
      ...defaultSinglePlayerCrossFlowExperience,
      getExperimentCohort: () => null,
    };
  }

  if (defaultMultiPlayerCrossFlowExperience.isEligible) {
    sendOperationalEvent(() => {
      Analytics.sendOperationalEvent({
        action: 'served',
        actionSubject: 'crossFlowExperience',
        source,
        containers: analyticsContainers,
        attributes: {
          experience: defaultMultiPlayerCrossFlowExperience.experience,
        },
      });
    });

    return {
      ...defaultMultiPlayerCrossFlowExperience,
      getExperimentCohort: () => null,
    };
  }

  if (defaultFreePpmCrossFlowExperience.isEligible) {
    sendOperationalEvent(() => {
      Analytics.sendOperationalEvent({
        action: 'served',
        actionSubject: 'crossFlowExperience',
        source,
        containers: analyticsContainers,
        attributes: {
          experience: defaultFreePpmCrossFlowExperience.experience,
        },
      });
    });

    return {
      ...defaultFreePpmCrossFlowExperience,
      getExperimentCohort: () => null,
    };
  }

  if (crossFlowForNewUserExperience.isEligible) {
    sendOperationalEvent(() => {
      Analytics.sendOperationalEvent({
        action: 'served',
        actionSubject: 'crossFlowExperience',
        source,
        containers: analyticsContainers,
        attributes: {
          experience: crossFlowForNewUserExperience.experience,
        },
      });
    });
    return {
      ...crossFlowForNewUserExperience,
      getExperimentCohort: () => null,
    };
  }

  if (crossFlowForOnboardingUserExperience.isEligible) {
    sendOperationalEvent(() => {
      Analytics.sendOperationalEvent({
        action: 'served',
        actionSubject: 'crossFlowExperience',
        source,
        containers: analyticsContainers,
        attributes: {
          experience: crossFlowForOnboardingUserExperience.experience,
        },
      });
    });

    return {
      ...crossFlowForOnboardingUserExperience,
      getExperimentCohort: () => null,
    };
  }
  return trelloCrossFlow;
};
